// Single source of truth for heading-text -> anchor slugification, used by
// both MarkdownPolicyDocument (assigning real heading `id`s at render time)
// and parseHeadingsToToc below (building PolicyDocumentLayout's `toc` prop)
// so the two can never diverge — a link in the TOC always points at a real
// heading id.
//
// ANCHOR_OVERRIDES is presentation-only: it does not decide what a public
// Rules anchor *is* (who-would-win-server's config/publicRules.js remains
// that authority) — it only maps a heading's *visible* text to the anchor
// that's already used everywhere else, for the small set of headings whose
// naive slug wouldn't match (the 8 numbered detailed-rule headings, whose
// anchor is a short internal-style slug, not a slug of the full display
// title; and "Images & Sharing", whose "&" handling is ambiguous under
// naive slugification). See src/test/utils/policyMarkdown.test.js.
const ANCHOR_OVERRIDES = {
  "1. Keep Battles About Entertainment, Not Real-World Harm": "real-world-harm",
  "2. No Sexual Exploitation or Unsafe Sexual Content": "sexual-safety",
  "3. No Hate or Discriminatory Degradation": "hate-and-discrimination",
  "4. No Targeted Harassment or Serious Threats": "harassment-and-threats",
  "5. No Encouragement of Self-Harm or Dangerous Real-World Harm": "self-harm-and-dangerous-activity",
  "6. No Extremist Recruitment or Facilitation of Serious Wrongdoing": "extremism-and-serious-wrongdoing",
  "7. Respect the Service": "platform-security",
  "8. Don't Spam, Scrape, Evade Limits, or Evade Enforcement": "spam-limits-and-enforcement-evasion",
  "Images & Sharing": "images-and-sharing",
};

function naiveSlug(text) {
  return text
    .toLowerCase()
    .replace(/'/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function slugifyHeading(text) {
  return ANCHOR_OVERRIDES[text] ?? naiveSlug(text);
}

// Small regex scan over raw markdown for `##`/`###` heading lines — no need
// for a full remark AST just to build a nested TOC. Consecutive `###` lines
// nest as children of the preceding `##` (matches the actual document
// structure: only "The Detailed Rules" has any `###` children).
export function parseHeadingsToToc(markdown) {
  const toc = [];
  let currentH2 = null;
  for (const line of markdown.split("\n")) {
    const h2 = /^##\s+(.*)$/.exec(line);
    const h3 = !h2 && /^###\s+(.*)$/.exec(line);
    if (h2) {
      const label = h2[1].trim();
      currentH2 = { id: slugifyHeading(label), label };
      toc.push(currentH2);
    } else if (h3 && currentH2) {
      const label = h3[1].trim();
      (currentH2.children ??= []).push({ id: slugifyHeading(label), label });
    }
  }
  return toc;
}
